import {Dayjs} from "dayjs";
import { ReportAdminService } from "app/services/report/ReportAdminService";
import { ResponseResultCode } from "app/model/response-api";
import { useEffect, useState } from "react";
import CountUp from "react-countup";
import ArcButton, { ButtonIcon, ButtonType } from "app/components/arc/ArcButton";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { numberFormatter } from "app/utils/stringUtil";
import DashboardLoading from "app/pages/db/modules/DashboardLoading";
import useInterval from "app/hooks/use-interval";
import { PcAdminService } from "app/services/pc/pcAdminService";

interface JoinData {
  participation: number;
  doRe100: number;
  fufl: number;
  interest: number;
}

interface props{
  displayTime : Dayjs;
}

export default function DbRe100Join({displayTime} : Readonly<props>) {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const initJoin: JoinData = { participation: 0, doRe100: 0, fufl: 0, interest: 0 };
  const [data, setData] = useState<JoinData>(initJoin);
  const [totalCompany, setTotalCompany] = useState<number>(0);

  useEffect(() => {
    fetch();
  }, []);

  useInterval(() => {
    fetch();
  }, 600000);

  function fetch() {
    Promise.all([fetchRE100(), fetchCompanyCount()])
    .then(([re100Data, companyCount]) => {
      setData({
        participation: re100Data.participation ?? 0,
        doRe100: re100Data.doRe100 ?? 0,
        interest: re100Data.interest ?? 0,
        fufl: re100Data.fufl ?? 0,
      });
      setTotalCompany(companyCount ?? 0);
    })
    .catch(() => {
      setData(initJoin);
      setTotalCompany(0);
    })
    .finally(() => {
      setIsLoading(false);
    });
  }

  async function fetchRE100() {
    try {
      const response = await ReportAdminService.getInstance().getParticipationCount();
      if (response && ResponseResultCode.Y === response.resultCode) {
        if (response.data) {
          return response.data;
        }
      }
      return { participation: 0, doRe100: 0, interest: 0, fufl: 0 };
    } catch {
      return { participation: 0, doRe100: 0, interest: 0, fufl: 0 };
    }
  }

  async function fetchCompanyCount(): Promise<number> {
    const res = await PcAdminService.getInstance().getEngUseStatus({
      searchIndsCmplxClCd: '',
      searchType: 'year',
      fromDt: moment().format('YYYY')
    });

    if (res && ResponseResultCode.Y === res.resultCode) {
      return res.data.length ?? 0;
    } else {
      return 0;
    }
  }

  return (
    <div className="cont">
      <DashboardLoading isLoading={isLoading} />
      <h2 className="titT2 bl"><span>산단 RE100 참여 현황</span></h2>
      <div className="count-info-lst2">
        <ul>
          <li className="bg1">
            <div>
              <span>RE100 참여</span>
              <em>
                <CountUp end={Number(data.participation ?? 0)} duration={2} >
                  {({ countUpRef }) => <strong ref={countUpRef} />}
                </CountUp><strong>/ {numberFormatter(totalCompany)}</strong>개
              </em>
            </div>
          </li>
          <li className="bg2">
            <div>
              <span>RE100 이행</span>
              <em>
                <CountUp end={data.doRe100 ?? 0} duration={2}>
                  {({ countUpRef }) => <strong ref={countUpRef} />}
                </CountUp><strong>/ {numberFormatter(totalCompany)}</strong>개
              </em>
            </div>
          </li>
          <li className="bg3">
            <div>
              <span>RE100 관심</span>
              <em>
                <CountUp end={Number(data.interest ?? 0)} duration={2}>
                  {({ countUpRef }) => <strong ref={countUpRef} />}
                </CountUp><strong>/ {numberFormatter(totalCompany)}</strong>개
              </em>
            </div>
          </li>
          <li className="bg4">
            <div>
              <span>RE100 달성</span>
              <em>
                <CountUp end={data.fufl ?? 0} duration={2}>
                  {({ countUpRef }) => <strong ref={countUpRef} />}
                </CountUp><strong>/ {numberFormatter(totalCompany)}</strong>개
              </em>
            </div>
          </li>
        </ul>
      </div>
      <div className="mgt10">
        <p className="reference-time">기준시간 : {displayTime.format("YYYY.MM.DD HH:mm")}</p>
      </div>
      <ArcButton
        type={ButtonType.custom}
        className="more-btn"
        icon={ButtonIcon.plus}
        hasIcon
        onClick={() => navigate('/ot/re100/status')}
      />
    </div>
  );
}